import { defineStore } from 'pinia';
import { ref } from 'vue';
import { personService } from '~/services/person-service';

import { useVaultStore } from '~/stores/person/vault/vault';
import { ModalNames } from '~/types/modals';
import type { Address, Aka, License, Person } from '~/types/person/person';
import type { EmailVerificationData, Phone } from '~/types/person/vault';
import { useContactsStore } from './person/contacts';

export const usePersonStore = defineStore('person', () => {
    const { showSuccess, showError } = useGlobalToast();
    const person = ref<Person>();
    const auth = useAuthStore();
    const vaultStore = useVaultStore();
    const contactStore = useContactsStore();
    const impersonationLoader = ref(false);
    const errorMessage = ref('');
    const imgViewerUrl = ref<string | null>(null);
    const impersonatedLicense = ref<string>('');
    const selectedPersonForLicense = ref<License | null>(null);
    const route = useRoute();

    /**
     * Check if currently impersonating by comparing loaded person to authenticated user
     * Returns the person ID if impersonating, or false if not
     */
    const impersonating = computed(() => {
        const actualUserId = auth.user?.identity?.id;
        const loadedPersonId = person.value?.id;

        if (!actualUserId || !loadedPersonId) {
            return false;
        }

        // Impersonating if the loaded person is different from the authenticated user
        return loadedPersonId !== actualUserId ? loadedPersonId : false;
    });

    /**
     * End impersonation by removing the person query parameter
     */
    async function endImpersonation() {
        impersonationLoader.value = true;

        // Clear vault data when ending impersonation
        const { clearVaultData } = useVaultStore();
        clearVaultData();

        // Get current path and remove person query parameter
        const currentPath = window.location.pathname;
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('person');

        // Build new URL without person param
        const newSearch = searchParams.toString();
        const newUrl = newSearch ? `${currentPath}?${newSearch}` : currentPath;

        // Reload the page without the URL parameter
        window.location.replace(newUrl);

        // No code after this will be executed.
    }

    /**
     * Start impersonation by adding/updating the person query parameter
     */
    async function startImpersonation(personId: number) {
        if (personId === person.value?.id) {
            return;
        }

        const router = useRouter();

        impersonationLoader.value = true;

        // Clear vault data when switching persons
        const { clearVaultData } = useVaultStore();
        clearVaultData();

        // Add/update the person query parameter
        await router.push({
            path: route.path,
            query: {
                ...route.query,
                person: personId.toString()
            }
        });

        await getPerson();

        await vaultStore.refreshVault();

        impersonationLoader.value = false;
    }

    /**
     * Get the person ID to fetch based on URL query param or authenticated user
     */
    function getPersonIdToFetch(): number | null {
        const authStore = useAuthStore();
        const actualUserId = authStore.user?.identity?.id;

        // Check actual URL for person param (more reliable than route.query which may be stale)
        let personParam: string | null = null;
        if (typeof window !== 'undefined') {
            const searchParams = new URLSearchParams(window.location.search);
            personParam = searchParams.get('person');
        } else {
            // Fallback to route.query for SSR
            personParam = route.query.person as string | null;
        }

        // If person query param exists and is valid, use it
        if (personParam) {
            const personId = parseInt(personParam);
            if (!isNaN(personId)) {
                return personId;
            }
        }

        // Otherwise use the authenticated user's ID
        return actualUserId || null;
    }

    /**
     * Fetch and set the person data based on URL query param or authenticated user
     */
    async function getPerson() {
        const personId = getPersonIdToFetch();

        // Don't make API call if we still don't have a valid personId
        if (!personId) {
            console.warn('getPerson called without valid personId, skipping API call');
            return;
        }

        try {
            person.value = await personService.getPerson(personId);
        } catch (error: any) {
            // During SSR, if we can't fetch person data due to auth issues, just log and continue
            if (process.server && error.message?.includes('SSR without authentication')) {
                return;
            }
            throw error;
        }
    }

    /**
     * Shared skeleton for person CRUD actions: guard on a loaded person, run the
     * service call, optionally refetch the person, toast the outcome.
     */
    async function runPersonAction<T>(
        action: (personId: number) => Promise<T>,
        options: { success?: string; failure: string; refetch?: boolean; rethrow?: boolean }
    ): Promise<T | undefined> {
        if (!person.value?.id) return;

        try {
            const result = await action(person.value.id);

            if (options.refetch) {
                await getPerson();
            }

            if (options.success) {
                showSuccess('Success', options.success);
            }

            return result;
        } catch (error) {
            showError('Error', (error instanceof Error && error.message) || options.failure);

            if (options.rethrow) {
                throw error;
            }

            return undefined;
        }
    }

    /**
     * Update the person data (dob, first_name, last_name, middle_name)
     * @param postData - The data to update (can be a partial of the Person type)
     */
    async function updatePerson(postData: Partial<Person>) {
        await runPersonAction(
            async (personId) => {
                await personService.updatePerson(personId, postData);
                person.value = await personService.getPerson(personId);
            },
            { success: 'Person updated successfully', failure: 'Failed to update person' }
        );
    }

    /**
     * Add an alias name to the person
     * @param postData - The alias name to add
     */
    async function addAka(postData: Aka) {
        await runPersonAction(
            async (personId) => {
                await personService.addAka(personId, postData);
                person.value = await personService.getPerson(personId);
                await auth.getMe();
            },
            { success: 'Alias name added', failure: 'Failed to add alias name', rethrow: true }
        );
    }

    /**
     * Delete an alias name from the person
     * @param akaId - The id of the alias name to delete
     */
    async function deleteAka(akaId: number) {
        await runPersonAction((personId) => personService.deleteAka(personId, akaId), {
            refetch: true,
            success: 'Alias name deleted',
            failure: 'Failed to delete alias name',
        });
    }

    /**
     * Update an alias name
     * @param akaId - The id of the alias name to update
     * @param postData - The data to update
     */
    async function updateAka(akaId: number, postData: Partial<Aka>): Promise<boolean> {
        const updated = await runPersonAction(
            async (personId) => {
                await personService.updateAka(personId, akaId, postData);
                return true;
            },
            { refetch: true, success: 'Alias name updated', failure: 'Failed to update alias name' }
        );

        return updated ?? false;
    }

    /**
     * Add an address to the person
     * @param postData - The address to add
     */
    async function addAddress(postData: Address) {
        await runPersonAction(
            async (personId) => {
                await personService.addAddress(personId, postData);
                person.value = await personService.getPerson(personId);
            },
            { success: 'Address added', failure: 'Failed to add address' }
        );
    }

    /**
     * Update an address
     * @param addressId - The id of the address to update
     * @param postData - The data to update
     */
    async function updateAddress(addressId: number, postData: Partial<Address>): Promise<boolean> {
        const updated = await runPersonAction(
            async (personId) => {
                await personService.updateAddress(personId, addressId, postData);
                return true;
            },
            { refetch: true, success: 'Address updated successfully', failure: 'Failed to update address' }
        );

        return updated ?? false;
    }

    /**
     * Delete an address from the person
     * @param addressId - The id of the address to delete
     */
    async function deleteAddress(addressId: number) {
        await runPersonAction((personId) => personService.deleteAddress(personId, addressId), {
            refetch: true,
            success: 'Address deleted',
            failure: 'Failed to delete address',
        });
    }

    /**
     * Delete a phone number from the person
     * @param phoneId - The id of the phone number to delete
     */
    async function deletePhone(phoneId: number) {
        await runPersonAction((personId) => personService.deletePhone(personId, phoneId), {
            refetch: true,
            success: 'Phone number deleted',
            failure: 'Failed to delete phone number',
        });
    }

    /**
     * Update a phone number
     * @param phoneId - The id of the phone number to update
     * @param postData - The data to update
     */
    async function updatePhone(phoneId: number, postData: Partial<Phone>) {
        await runPersonAction((personId) => personService.updatePhone(personId, phoneId, postData), {
            refetch: true,
            success: 'Phone number updated',
            failure: 'Failed to update phone number',
        });
    }

    /**
     * Send a verification code to a phone number
     * @param postData - The phone number to send the verification code to
     * @returns A toast message
     */
    async function sendPhoneVerificationCode(
        isSms: boolean,
        postData: { phone_number: string; country_code: string; method: string; fromExposure?: boolean; exposureData?: any },
        isResend: boolean = false
    ) {
        await runPersonAction(
            async (personId) => {
                await personService.sendPhoneVerificationCode(personId, postData);

                if (!isResend) {
                    useDynamicDialog(ModalNames.PHONE_VERIFICATION, postData);
                }
            },
            {
                success: isSms ? 'Successfully sent phone verification code' : 'Successfully sent phone call verification code',
                failure: 'Failed to send phone verification code',
                rethrow: true,
            }
        );
    }

    /**
     * Verify the OTP code and add the phone number to the person
     * @param payload - The phone number and OTP code to verify
     */
    async function verifyPhoneOtp(payload: { country_code: string; phone_number: string; code: string }) {
        await runPersonAction((personId) => personService.verifyPhoneOtp(personId, payload), {
            refetch: true,
            success: 'Phone number added',
            failure: 'Failed to verify phone OTP',
            rethrow: true,
        });
    }

    async function addPhoneFromExposure(payload: { country_code: string; phone_number: string }): Promise<void> {
        await runPersonAction((personId) => personService.addPhoneWithoutVerification(personId, payload), {
            refetch: true,
            success: 'Phone number added to vault',
            failure: 'Failed to add phone number',
            rethrow: true,
        });
    }

    /**
     * Send a verification code to an email and open the PIN modal
     * @param email - The email to send the verification code to
     * @returns A toast message
     */
    async function sendEmailVerificationCode(email: string, isResend: boolean = false, additionalData?: any) {
        try {
            await personService.sendEmailVerificationCode(email);

            if (!isResend) {
                useDynamicDialog(ModalNames.EMAIL_VERIFICATION, { email, ...additionalData });
            }

            showSuccess("Success", 'Email verification code sent');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to send email verification code');
            throw error;
        }
    }

    /**
     * Verify the OTP code and add the email to the person
     * @param payload - The email verification data to verify
     */
    async function verifyEmailOtp(payload: EmailVerificationData) {
        try {
            await runPersonAction((personId) => personService.verifyEmailOtp(personId, payload), {
                refetch: true,
                success: 'Email added',
                failure: 'Failed to verify email OTP',
                rethrow: true,
            });
        } catch (error) {
            errorMessage.value = error instanceof Error ? error.message : '';
            throw error;
        }
    }

    /**
     * Update an email
     * @param emailId - The id of the email to update
     * @param postData - The data to update
     */
    async function updateEmail(emailId: number, postData: { primary: boolean }) {
        await runPersonAction((personId) => personService.updateEmail(personId, emailId, postData), {
            refetch: true,
            success: 'Email updated successfully',
            failure: 'Failed to update email',
        });
    }

    /**
     * Delete an email from the person
     * @param emailId - The id of the email to delete
     */
    async function deleteEmail(emailId: number) {
        await runPersonAction((personId) => personService.deleteEmail(personId, emailId), {
            refetch: true,
            success: 'Email deleted',
            failure: 'Failed to delete email',
        });
    }

    async function fetchStates(id: number) {
        return runPersonAction(() => personService.fetchStates(id), { failure: 'Failed to fetch states' });
    }

    async function fetchStatesByCode(countryCode: string) {
        return runPersonAction(() => personService.fetchStatesByCode(countryCode), { failure: 'Failed to fetch states' });
    }

    function useSearchPhrases() {
        let me = auth?.user
        if (!me) return [];

        const firstName = me.identity.first_name;
        const lastName = me.identity.last_name;
        const fullName = `${firstName} ${lastName}`;
        const city = me.identity.location?.city;
        const region = me.identity.location?.region;
        const postal = me.identity.location?.postal_code;
        const email = me.identity.email;

        return [
            fullName,
            city ? `${fullName} ${city}` : null,
            region ? `${fullName} ${region}` : null,
            postal ? `${lastName} ${postal}` : null,
            email
        ].filter(Boolean);
    }

    return {
        person,
        getPerson,
        updatePerson,
        addAka,
        updateAka,
        deleteAka,
        addAddress,
        deleteAddress,
        deletePhone,
        deleteEmail,
        sendEmailVerificationCode,
        verifyEmailOtp,
        sendPhoneVerificationCode,
        verifyPhoneOtp,
        addPhoneFromExposure,
        updateAddress,
        updatePhone,
        updateEmail,
        impersonationLoader,
        imgViewerUrl,
        selectedPersonForLicense,
        impersonating,
        impersonatedLicense,
        startImpersonation,
        endImpersonation,
        fetchStates,
        fetchStatesByCode,
        errorMessage,
        useSearchPhrases
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePersonStore, import.meta.hot))
  }
